import React, { useState, useEffect } from "react";
import { Container, Card, Form, Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@apollo/client";
import { toast } from "react-toastify";
import { CREATE_PROJECT_GROUP_MUTATION } from "../../../../graphql/mutations";
import ProjectGroupDetails from "./sections/ProjectGroupDetails";

const AddProjectGroupPage = ({
  env,
  handleEnvChange,
  currentTenant,
  isLoading,
  isError,
  tenants,
  countries,
  countriesLoading,
  countriesError,
  groupAssetTypes,
  groupAssetTypesLoading,
  groupAssetTypesError,
  groups
}) => {
  const navigate = useNavigate();
  const [existingPublicIds, setExistingPublicIds] = useState([]);

  const [formData, setFormData] = useState({
    name: "",
    groupAssetTypeId: "",
    segment: "",
    region: "",
    publicId: "",
    operator: "",
    longitude: "",
    latitude: "",
    country: "",
    tenantId: "",
    tenantName: ""
  });
  const [formErrors, setFormErrors] = useState({});

  // extract existing publicIds from groups when component mounts or groups change
  useEffect(() => {
    if (groups && groups.length > 0) {
      try {
        const parsedGroups = typeof groups === "string" ? JSON.parse(groups) : groups;
        const ids = parsedGroups.filter((group) => group.publicId !== undefined && group.publicId !== null).map((group) => group.publicId);
        setExistingPublicIds(ids);
      } catch (error) {
        console.error("Error parsing groups data:", error);
        setExistingPublicIds([]);
      }
    } else {
      setExistingPublicIds([]);
    }
  }, [groups]);

  const [createProjectGroup, { loading: mutationLoading }] = useMutation(CREATE_PROJECT_GROUP_MUTATION);

  const handleFormChange = (field, value) => {
    setFormData((prevData) => ({
      ...prevData,
      [field]: value
    }));

    // clear error for this field when user makes changes
    if (formErrors[field]) {
      setFormErrors((prev) => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    const requiredFields = ["name", "segment", "tenantId", "groupAssetTypeId", "country"];

    requiredFields.forEach((field) => {
      if (!formData[field]) {
        errors[field] = "This field is required";
      }
    });

    // check for duplicate publicId
    if (formData.publicId) {
      const publicIdValue = parseInt(formData.publicId, 10);
      if (existingPublicIds.includes(publicIdValue)) {
        errors.publicId = "This Public ID already exists";
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix all form errors before submitting");
      return;
    }

    try {
      const publicIdValue = formData.publicId ? parseInt(formData.publicId, 10) : 0;

      const { data } = await createProjectGroup({
        variables: {
          tenantId: formData.tenantId,
          projectGroup: {
            name: formData.name,
            groupAssetTypeId: formData.groupAssetTypeId,
            region: formData.region,
            publicId: publicIdValue,
            operator: formData.operator,
            longitude: formData.longitude,
            latitude: formData.latitude,
            country: formData.country
          }
        }
      });

      if (data?.createProjectGroup) {
        toast.success(`Project Group created successfully`);
        navigate("/projects");
        handleEnvChange(env, formData.tenantId, formData.tenantName);
      }
    } catch (error) {
      toast.error(`Failed to create project group: ${error.message}`);
    }
  };

  return (
    <Container className="mt-1">
      <h4 className="mb-4">Add New Project Group</h4>
      <Card className="border mb-3">
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <ProjectGroupDetails
              isLoading={isLoading}
              isError={isError}
              countriesLoading={countriesLoading}
              countriesError={countriesError}
              countries={countries || []}
              assetTypesLoading={groupAssetTypesLoading}
              assetTypesError={groupAssetTypesError}
              assetTypes={groupAssetTypes || []}
              onFormChange={handleFormChange}
              tenants={tenants}
              formData={formData}
              formErrors={formErrors}
              existingPublicIds={existingPublicIds}
            />
            <div className="d-grid">
              <Button variant="primary" type="submit" className="mt-3 mb-3 mx-auto" style={{ width: "25%" }} disabled={mutationLoading}>
                {mutationLoading ? "Creating Project Group..." : "Create Project Group"}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default AddProjectGroupPage;
